<script setup>
/**
 * RED-310 — zone A "Aruande tingimused" of the Analüütika drawer. Every
 * field binds straight to `analyticsStore.form`; the primary "Koosta aruanne"
 * sits bottom-right, "Tühjenda väljad" is the quiet text button beside it.
 * Validation never toasts and never clears what the user typed.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalyticsStore } from '@/stores/analyticsStore'
import LocationUnitsPicker from './LocationUnitsPicker.vue'
import MapFilterChips from './MapFilterChips.vue'
import PeriodPicker from './PeriodPicker.vue'

const { t } = useI18n()
const store = useAnalyticsStore()

const reportOptions = computed(() =>
  store.reportOptions.map(r => ({
    ...r,
    label: r.label ?? t('messages.transactions.analytics.form.report_korteriomandid'),
  })),
)

const groupByOptions = computed(() => [
  {
    value: 'band',
    label: store.selectedReport?.group_label ?? t('messages.transactions.analytics.form.group_band'),
  },
  { value: 'period', label: t('messages.transactions.analytics.form.group_period') },
])

const isMulti = computed(() => store.form.periodMode === 'multi')

const trimHint = computed(() =>
  t('messages.transactions.analytics.form.trim_hint', {
    p: Number(store.form.trim),
    basis: t(
      `messages.transactions.analytics.form.trim_basis.${store.selectedReport?.measure ?? 'price_per_m2'}`,
    ),
  }),
)
</script>

<template>
  <form
    class="flex flex-col gap-5"
    data-testid="txn-analytics-form"
    @submit.prevent="store.run()"
  >
    <h3 class="text-xl font-medium text-gray-800">
      {{ $t('messages.transactions.analytics.form.title') }}
    </h3>

    <Fluid class="flex flex-col gap-5">
      <div class="flex flex-col gap-1">
        <label for="txn-analytics-report" class="text-sm font-medium text-gray-700">
          {{ $t('messages.transactions.analytics.form.report') }}
        </label>
        <Select
          :model-value="store.form.report"
          label-id="txn-analytics-report"
          :options="reportOptions"
          option-label="label"
          option-value="key"
          option-disabled="disabled"
          :loading="store.optionsLoading"
          data-testid="txn-analytics-report"
          @update:model-value="store.setReport($event)"
        >
          <template #option="{ option }">
            <span class="flex w-full items-center justify-between gap-3">
              <span :class="{ 'text-gray-400': option.disabled }">{{ option.label }}</span>
              <Tag
                v-if="option.disabled"
                :value="$t('messages.transactions.analytics.form.report_later')"
                severity="secondary"
                class="text-xs"
              />
            </span>
          </template>
        </Select>
        <p class="text-xs text-gray-500">
          {{ $t('messages.transactions.analytics.form.report_later_hint') }}
        </p>
      </div>

      <LocationUnitsPicker />

      <PeriodPicker />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label for="txn-analytics-trim" class="text-sm font-medium text-gray-700">
            {{ $t('messages.transactions.analytics.form.trim') }}
          </label>
          <Select
            v-model="store.form.trim"
            label-id="txn-analytics-trim"
            :options="store.trimOptions"
            option-label="label"
            option-value="value"
            data-testid="txn-analytics-trim"
          />
          <p
            class="text-xs text-gray-500 min-h-4"
            :class="{ invisible: !Number(store.form.trim) }"
            data-testid="txn-analytics-trim-hint"
          >
            {{ trimHint }}
          </p>
        </div>

        <div class="flex flex-col gap-1">
          <label for="txn-analytics-group-by" class="text-sm font-medium text-gray-700">
            {{ $t('messages.transactions.analytics.form.group_by') }}
          </label>
          <Select
            v-model="store.form.groupBy"
            label-id="txn-analytics-group-by"
            :options="groupByOptions"
            option-label="label"
            option-value="value"
            :disabled="isMulti"
            data-testid="txn-analytics-group-by"
          />
          <p class="text-xs text-gray-500 min-h-4" :class="{ invisible: !isMulti }">
            {{ $t('messages.transactions.analytics.form.group_by_multi_hint') }}
          </p>
        </div>
      </div>

      <MapFilterChips />

      <div class="flex items-center justify-end gap-2 pt-1">
        <Button
          type="button"
          :label="$t('messages.transactions.analytics.form.reset')"
          variant="text"
          severity="secondary"
          size="small"
          class="w-auto!"
          data-testid="txn-analytics-reset"
          @click="store.reset()"
        />
        <Button
          type="submit"
          :label="$t('messages.transactions.analytics.form.run')"
          icon="pi pi-chart-bar"
          size="small"
          class="w-auto!"
          :loading="store.state === 'LOADING'"
          :disabled="!store.canRun"
          data-testid="txn-analytics-run"
        />
      </div>
    </Fluid>
  </form>
</template>
